// The table's sound, played (issue 1161): always mounted at the table, whatever the dock holds. Each screen plays what
// the table's state says — the music playlist, the ambience loop, the effects as they are fired — from the server's
// `startAt`, at this person's own volume (their mixer, kept in this browser). The DM's screen (and a co-DM's with
// `notes`) says when a playlist's track has ended, so the table moves on once; nobody else's does.
//
// A browser refuses to start sound before the page has been clicked: when it does, one button says so — "Click to hear
// the table's sound" — and a click starts everything that should be playing.
import { useEffect, useRef, useState } from 'react';
import { emptyAudio, PLAYLIST_CHANNELS, type ChannelPlay, type PlaylistChannel } from '@dragons-table/vtt-shared';
import { useTableState } from '../table/TableState';
import { mayActAs } from '../table/mayAct';
import { effectIsFresh, loadMixer, onMixer, playOffset, releaseSounds, soundUrl, volumeOf, type Mixer } from '../sound';

/** The key of what a channel is playing now: a change of track or a restart is a new key. */
const keyOf = (p: ChannelPlay | null) => (p ? `${p.tracks[p.index]?.id ?? ''}@${p.startAt}` : '');

export function SoundPlayer() {
  // `?? emptyAudio()`: a surface mounted with a table state from before issue 1161 (a test's) hears silence.
  const ts = useTableState();
  const { act, role, powers } = ts;
  const audio = ts.audio ?? emptyAudio();
  const clockSkew = ts.clockSkew ?? 0;
  const drives = mayActAs(role, powers ?? null, 'audio.next'); // `notes`'s, whichever board a co-DM sees
  const [mixer, setMixer] = useState<Mixer>(() => loadMixer());
  useEffect(() => onMixer(setMixer), []);
  const [blocked, setBlocked] = useState(false);
  const els = useRef<Record<PlaylistChannel, HTMLAudioElement | null>>({ music: null, ambience: null });
  const playing = useRef<Record<PlaylistChannel, string>>({ music: '', ambience: '' });
  const skew = useRef(clockSkew);
  skew.current = clockSkew;

  const start = (el: HTMLAudioElement) => {
    const p = el.play() as Promise<void> | undefined;
    p?.catch?.((err: unknown) => { if ((err as { name?: string })?.name === 'NotAllowedError') setBlocked(true); });
  };

  // Each playlist channel: a new track (or a restart) is fetched with the bearer and started where the table is now.
  const musicKey = keyOf(audio.music); const ambienceKey = keyOf(audio.ambience);
  useEffect(() => {
    for (const ch of PLAYLIST_CHANNELS) {
      const el = els.current[ch];
      const play = audio[ch];
      const key = keyOf(play);
      if (!el || playing.current[ch] === key) continue;
      playing.current[ch] = key;
      if (!play) { el.pause(); el.removeAttribute('src'); el.removeAttribute('data-track'); continue; }
      const track = play.tracks[play.index]!;
      el.loop = play.loop && play.tracks.length === 1;
      el.dataset.track = track.id;
      void soundUrl(track.id).then((url) => {
        if (!url || playing.current[ch] !== key) return;
        el.src = url;
        el.onloadedmetadata = () => { try { el.currentTime = playOffset(play, el.duration, Date.now(), skew.current); } catch { /* not seekable yet */ } };
        start(el);
      });
    }
  }, [musicKey, ambienceKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Each track's object URL is kept while a playlist holds it or an effect of it still sounds, and released after (the
  // second 2026-10-10 review); leaving the table releases every one.
  const sounding = useRef(new Map<string, number>());
  const playlistIds = PLAYLIST_CHANNELS.flatMap((ch) => audio[ch]?.tracks.map((t) => t.id) ?? []).sort().join(',');
  const release = () => {
    const keep = new Set<string>([...sounding.current.keys()]);
    for (const ch of PLAYLIST_CHANNELS) for (const t of audio[ch]?.tracks ?? []) keep.add(t.id);
    releaseSounds(keep);
  };
  const releaseRef = useRef(release);
  releaseRef.current = release;
  useEffect(() => { releaseRef.current(); }, [playlistIds]);
  useEffect(() => () => releaseSounds(new Set()), []);

  // The mixer: this person's volume a channel, and mute; applied at once, never sent.
  useEffect(() => {
    for (const ch of PLAYLIST_CHANNELS) {
      const el = els.current[ch];
      if (el) { el.volume = volumeOf(mixer, ch); el.muted = mixer.muted; }
    }
  }, [mixer]);

  // A playlist's track ended: the driving screen moves the table on, from where it stands (the server takes it once).
  const ended = (ch: PlaylistChannel) => {
    const play = audio[ch];
    if (drives && play && !(play.loop && play.tracks.length === 1)) act({ kind: 'audio.next', channel: ch, from: play.index, at: play.startAt });
  };

  // Effects: each fired one played once, as it arrives; the ones already fired when this screen came are not.
  const heard = useRef<Set<string> | null>(null);
  useEffect(() => {
    const keys = audio.effects.map((e) => `${e.id}@${e.startAt}`);
    if (heard.current === null) { heard.current = new Set(keys); return; }
    for (const e of audio.effects) {
      const key = `${e.id}@${e.startAt}`;
      if (heard.current.has(key)) continue;
      heard.current.add(key);
      if (!effectIsFresh(e.startAt, Date.now(), skew.current)) continue;
      sounding.current.set(e.id, (sounding.current.get(e.id) ?? 0) + 1);
      const done = () => {
        const left = (sounding.current.get(e.id) ?? 1) - 1;
        if (left > 0) sounding.current.set(e.id, left); else sounding.current.delete(e.id);
        releaseRef.current();
      };
      void soundUrl(e.id).then((url) => {
        if (!url) { done(); return; }
        const one = new Audio(url);
        one.onended = done; one.onerror = done;
        one.dataset.testid = 'sound-effect';
        one.volume = volumeOf(mixer, 'effect');
        one.muted = mixer.muted;
        start(one);
      });
    }
  }, [audio.effects]); // eslint-disable-line react-hooks/exhaustive-deps

  const unblock = () => {
    setBlocked(false);
    for (const ch of PLAYLIST_CHANNELS) { const el = els.current[ch]; if (el && audio[ch] && el.src) start(el); }
  };

  return (
    <>
      <audio ref={(el) => { els.current.music = el; }} data-testid="sound-music" preload="auto" onEnded={() => ended('music')} hidden />
      <audio ref={(el) => { els.current.ambience = el; }} data-testid="sound-ambience" preload="auto" onEnded={() => ended('ambience')} hidden />
      {blocked && (audio.music || audio.ambience) && (
        <p role="status" className="border-b border-stone-800 bg-stone-900 px-3 py-1 text-sm">
          <button type="button" data-testid="sound-unblock" className="rounded border border-amber-600 px-2 py-0.5 text-xs text-amber-300 hover:bg-stone-800" onClick={unblock}>
            Click to hear the table&apos;s sound
          </button>
        </p>
      )}
    </>
  );
}
